import React from 'react';
import { If } from 'components/if-statement';
import {
  Container,
  ErrorMessage,
  TextInput as StyledTextInput,
  Icon as StyledIcon,
  Description,
  TextInputContainer,
  TextInputLabel,
} from 'components/text-input/style';
import { omitStyles, pickStyles } from 'utils';
import { noop, isFunction } from 'lodash';
import {
  useTextInput,
  TextInputProps as LogicTextInputProps,
} from 'components/text-input/logic';
import { withForwardRef } from 'hoc';

export type TextInputProps = StyledTextInputProps &
  React.HTMLProps<HTMLInputElement>;

interface StyledTextInputProps extends LogicTextInputProps {
  noBorder?: boolean;
}

function TextInput({
  style,
  forwardedRef,
  noBorder = false,
  ...props
}: TextInputProps) {
  const {
    description,
    invalid,
    iconLeft,
    iconRight,
    label,
    errorMessage,
    withForm,
    primary,
    secondary,
  } = useTextInput(props);

  const LeftIcon = <StyledIcon>{renderLeftIcon() || null}</StyledIcon>;
  const RightIcon = <StyledIcon>{renderRightIcon() || null}</StyledIcon>;

  const inputTypeProperties = {
    ...(primary && secondary ? { primary: false } : {}),
    invalid,
    noBorder,
  };

  const labelProperties = {
    invalid,
    ...props,
    ...inputTypeProperties,
  };

  const inputProperties = {
    ...props,
    ...inputTypeProperties,
  };

  const containerStyle = omitStyles(
    style,
    'backgroundColor',
    'height',
    'color',
    'opacity',
  );

  const inputContainerStyle = pickStyles(style, 'backgroundColor', 'height');
  const textInputStyle = pickStyles(style, 'color', 'opacity');

  function renderRightIcon() {
    return isFunction(iconRight) ? iconRight(props.value) : iconRight;
  }

  function renderLeftIcon() {
    return isFunction(iconLeft) ? iconLeft(props.value) : iconLeft;
  }

  return (
    <Container withForm={withForm} style={containerStyle}>
      {Boolean(label) && (
        <TextInputLabel {...labelProperties}>{label}</TextInputLabel>
      )}
      <TextInputContainer {...inputProperties} style={inputContainerStyle}>
        <If condition={iconLeft}>{LeftIcon}</If>

        <StyledTextInput
          {...inputProperties}
          style={textInputStyle}
          ref={forwardedRef}
        />

        <If condition={iconRight}>{RightIcon}</If>
      </TextInputContainer>

      <If condition={description}>
        <Description>{description}</Description>
      </If>

      <If condition={invalid}>
        <ErrorMessage>{errorMessage}</ErrorMessage>
      </If>
    </Container>
  );
}

TextInput.defaultProps = {
  errorMessage: '',
  description: '',
  value: '',
  label: '',
  onBlur: noop,
  onChange: noop,
  onFocus: noop,
  noUnderline: false,
  placeholder: '',
  iconLeft: null,
  iconRight: null,
  disabled: false,
  primary: true,
  secondary: false,
  invalid: false,
  style: {},
};

TextInput.Label = StyledTextInput.Label;

export default withForwardRef<any, TextInputProps>(TextInput);
